// Libs
import classNames from 'classnames/bind';
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Box } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { Loading } from '~/components/common';
import { VehicleSchedule } from '~/components/module/schedule';
import { AvailableOrders, ItemOrderSchedule } from '~/components/module/order';
// Others
import { orderActions } from '~/thunks/order/orderSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { IOrderEventList } from '~/utils/interface/order';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { getCurrentWeekRange } from '~/utils/helper/common';
import { convertJobToSchedule } from '~/thunks/job/jobThunk';
import { SCHEDULE_PERMISSIONS } from '~/utils/constants/permissions';
import { IBodyUpdate, IRangeDate } from '~/utils/interface/common';
import { IFormConvertJobToSchedule } from '~/utils/interface/jobs';
// Styles, images, icons
import styles from './Schedule.module.scss';

const cx = classNames.bind(styles);

const Schedule = () => {
  //#region Destructuring Props
  const { canViewSchedules } = usePermissions(SCHEDULE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/schedule', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeOrder, setActiveOrder] = useState<IOrderEventList | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [rangeDate, setRangeDate] = useState<IRangeDate>(getCurrentWeekRange());
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const completelyInside: CollisionDetection = (args) => {
    const { collisionRect, droppableRects, droppableContainers } = args;

    const collisions = [];

    for (const droppable of droppableContainers) {
      const rect = droppableRects.get(droppable.id);
      if (!rect) continue;

      const isInside =
        collisionRect.left >= rect.left &&
        collisionRect.right <= rect.right &&
        collisionRect.top >= rect.top &&
        collisionRect.bottom <= rect.bottom;

      if (isInside) {
        collisions.push({
          id: droppable.id,
          data: { droppableContainer: droppable },
        });
      }
    }

    return collisions;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(MouseSensor),
    useSensor(TouchSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    setActiveOrder(null);
    const { active, over } = event;

    if (over) {
      const item = active?.data?.current as IOrderEventList;

      const body: IBodyUpdate<IFormConvertJobToSchedule> = {
        id: item?.id,
        body: {
          // startDate: item?.startDate || undefined,
          // startTime: item?.startTime || undefined,
        },
      };

      handleConvertJobToSchedule(body);
    }
  };

  const handleConvertJobToSchedule = (body: IBodyUpdate<IFormConvertJobToSchedule>) => {
    setIsLoading(true);

    dispatch(convertJobToSchedule(body))
      .unwrap()
      .then(() => {
        dispatch(orderActions.setRefreshOrderEventList(true));
        dispatch(scheduleActions.setRefreshScheduleList(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <DndContext
      onDragStart={() => setIsDragging(true)}
      onDragCancel={() => {
        setIsDragging(false);
        setActiveOrder(null);
      }}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      collisionDetection={completelyInside}
    >
      <Box id='schedulePage' className={cx('container')}>
        <Box className={cx('viewOrder')}>
          <AvailableOrders
            isDragging={isDragging}
            onStartDrag={setActiveOrder}
            rangeDate={rangeDate}
            isView={canViewSchedules}
          />
        </Box>

        <Box className={cx('viewSchedule')}>
          <VehicleSchedule rangeDate={rangeDate} setRangeDate={setRangeDate} isView={canViewSchedules} />
        </Box>
      </Box>

      {isLoading && <Loading />}

      <DragOverlay>{activeOrder ? <ItemOrderSchedule item={activeOrder} /> : null}</DragOverlay>
    </DndContext>
  );
};

export default Schedule;
